import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, User, BookOpen, CreditCard, Calendar, Play, FileText, Award, Percent, Clock } from 'lucide-react';
import MainModel from "../../../../components/Models/main-model";
import { closeStudentReportModel } from "../../../../modules/admin-modules/models/Actions";
import { studentPerformanceRequest, monthlyReportRequest } from "../../../../modules/admin-modules/reports/Actions";

const StudentReport = ({ selectedData, student_id }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
  const [videoSearchTerm, setVideoSearchTerm] = useState('');
  const paymentSearchRef = useRef('');
  const videoSearchRef = useRef('');
  
  const userData = useSelector((state) => state.reports.performance);
  const isLoading = useSelector((state) => state.reports.isLoading);
  const openUserData = useSelector((state) => state.model.isOpen18);
  const dispatch = useDispatch();
  
  const baseUrl = "https://asquera.com/asquera";

  const {
    payment = [],
    attendance = [],
    payment_exception,
    last_videos = [],
    homeworks = [],
    exams = []
  } = userData || {};

  useEffect(() => {
    if (student_id) dispatch(studentPerformanceRequest(student_id));
  }, [student_id]);

  const closeFunc = () => {
    dispatch(closeStudentReportModel());
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'videos', label: 'Videos', icon: Play },
    { id: 'exams', label: 'Exams', icon: Award }
  ];

  const getExamStats = () => {
    if (!exams || exams.length === 0) return null;
    const firstExam = exams[0];
    return firstExam?.student_exam?.[0] || null;
  };

  const examStats = getExamStats();

  // Memoized filtered results
  const filteredPayments = useMemo(() => {
    return payment.filter(item => 
      item.payment_subject?.name?.toLowerCase().includes(paymentSearchTerm.toLowerCase()) ||
      item.paid_amount?.toString().includes(paymentSearchTerm) ||
      new Date(item.created_at).toLocaleDateString().includes(paymentSearchTerm)
    );
  }, [payment, paymentSearchTerm]);

  const filteredVideos = useMemo(() => {
    return last_videos.filter(item => 
      item.video?.title?.toLowerCase().includes(videoSearchTerm.toLowerCase()) ||
      item.views_count?.toString().includes(videoSearchTerm) ||
      new Date(item.created_at).toLocaleDateString().includes(videoSearchTerm)
    );
  }, [last_videos, videoSearchTerm]);

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <div className={`bg-gradient-to-br from-${color}-50 to-${color}-100 p-6 rounded-xl border border-${color}-200 shadow-sm hover:shadow-md transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-${color}-600 text-sm font-medium mb-1`}>{title}</p>
          <p className={`text-2xl font-bold text-${color}-900`}>{value}</p>
        </div>
        <Icon className={`w-8 h-8 text-${color}-500`} />
      </div>
    </div>
  );

  const SectionCard = ({ title, children, icon: Icon }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-orange-400 to-orange-500 px-6 py-4">
        <div className="flex items-center space-x-3">
          <Icon className="w-5 h-5 text-white" />
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );

  const ItemList = ({ items, renderItem, emptyMessage, showAll = false }) => (
    <div className="space-y-3">
      {items && items.length > 0 ? (
        (showAll ? items : items.slice(0, 5)).map((item, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            {renderItem(item, index)}
          </div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{emptyMessage}</p>
        </div>
      )}
      {!showAll && items && items.length > 5 && (
        <p className="text-sm text-gray-500 text-center mt-3">
          +{items.length - 5} more items
        </p>
      )}
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Payments"
          value={payment?.length || 0}
          icon={CreditCard}
          color="green"
        />
        <StatCard
          title="Attendance"
          value={attendance?.length || 0}
          icon={Calendar}
          color="orange"
        />
        <StatCard
          title="Videos Watched"
          value={last_videos?.length || 0}
          icon={Play}
          color="purple"
        />
        <StatCard
          title="Exams Taken"
          value={exams?.length || 0}
          icon={Award}
          color="orange"
        />
      </div>

      {/* Payment Exception */}
      {payment_exception && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Percent className="w-6 h-6 text-amber-600" />
            <div>
              <h3 className="text-lg font-semibold text-amber-900">Payment Exception</h3>
              <p className="text-amber-700 font-medium">{payment_exception.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* Exam Performance */}
      {examStats && (
        <SectionCard title="Latest Exam Performance" icon={Award}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{examStats.total_exams_questions || 0}</p>
              <p className="text-sm text-orange-800">Total Questions</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{examStats.total_right_answers || 0}</p>
              <p className="text-sm text-green-800">Correct Answers</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{examStats.percentage || 0}%</p>
              <p className="text-sm text-purple-800">Success Rate</p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Recent Homeworks" icon={BookOpen}>
          <ItemList
            items={homeworks}
            renderItem={(homework) => (
              <>
                <div className="flex items-center space-x-3">
                  <BookOpen className="w-4 h-4 text-orange-500" />
                  <span className="font-medium text-gray-900">{homework.name}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  homework.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {homework.active ? 'Active' : 'Completed'}
                </span>
              </>
            )}
            emptyMessage="No homework assignments found"
          />
        </SectionCard>

        <SectionCard title="Recent Videos" icon={Play}>
          <ItemList
            items={last_videos}
            renderItem={(video) => (
              <>
                <div className="flex items-center space-x-3">
                  <Play className="w-4 h-4 text-purple-500" />
                  <span className="font-medium text-gray-900">{video.video?.title}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  video.seen ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {video.seen ? 'Watched' : 'Pending'}
                </span>
              </>
            )}
            emptyMessage="No videos watched yet"
          />
        </SectionCard>
      </div>
    </div>
  );

  const renderPayments = () => {
    const handlePaymentSearch = () => {
      setPaymentSearchTerm(paymentSearchRef.current);
    };

    const handlePaymentClear = () => {
      paymentSearchRef.current = '';
      setPaymentSearchTerm('');
    };

    return (
      <SectionCard title="Payment History" icon={CreditCard}>
        {/* Search Input */}
        <div className="mb-4">
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <input
                key="payment-search-input"
                type="text"
                placeholder="Search payments by name, amount, or date..."
                defaultValue=""
                onChange={(e) => { paymentSearchRef.current = e.target.value; }}
                onKeyPress={(e) => e.key === 'Enter' && handlePaymentSearch()}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              onClick={handlePaymentSearch}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Search
            </button>
            {(paymentSearchRef.current || paymentSearchTerm) && (
              <button
                onClick={handlePaymentClear}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        {paymentSearchTerm && (
          <div className="mb-3 text-sm text-gray-600">
            Found {filteredPayments.length} payment{filteredPayments.length !== 1 ? 's' : ''} for "{paymentSearchTerm}"
          </div>
        )}

        <div className="max-h-96 overflow-y-auto">
          <ItemList
            items={filteredPayments}
            renderItem={(paymentItem) => (
              <>
                <div>
                  <p className="font-medium text-gray-900">{paymentItem.payment_subject?.name}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(paymentItem.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">EGP {paymentItem.paid_amount}</p>
                  {paymentItem.remaining_amount !== 0 && (
                    <p className={`text-sm ${paymentItem.remaining_amount > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {paymentItem.remaining_amount > 0 ? 'Due: ' : 'Credit: '}EGP {Math.abs(paymentItem.remaining_amount)}
                    </p>
                  )}
                </div>
              </>
            )}
            emptyMessage={paymentSearchTerm ? "No payments match your search" : "No payment records found"}
            showAll={true}
          />
        </div>
      </SectionCard>
    );
  };

  const renderAttendance = () => (
    <SectionCard title="Attendance Records" icon={Calendar}>
      <ItemList
        items={attendance}
        renderItem={(attendanceItem) => (
          <>
            <div>
              <p className="font-medium text-gray-900">{attendanceItem.lecture?.title}</p>
              <p className="text-sm text-gray-500">
                {attendanceItem.actual_time ? 
                  new Date(attendanceItem.actual_time).toLocaleString() :
                  new Date(attendanceItem.created_at).toLocaleString()
                }
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                attendanceItem.attend ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {attendanceItem.attend ? 'Present' : 'Absent'}
              </span>
              {attendanceItem.parent_comment && (
                <span className="text-xs text-gray-500" title={attendanceItem.parent_comment}>
                  💬
                </span>
              )}
            </div>
          </>
        )}
        emptyMessage="No attendance records found"
      />
    </SectionCard>
  );

  const renderVideos = () => {
    const handleVideoSearch = () => {
      setVideoSearchTerm(videoSearchRef.current);
    };

    const handleVideoClear = () => {
      videoSearchRef.current = '';
      setVideoSearchTerm('');
    };

    return (
      <SectionCard title="Video Watch History" icon={Play}>
        {/* Search Input */}
        <div className="mb-4">
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <input
                key="video-search-input"
                type="text"
                placeholder="Search videos by title, views, or date..."
                defaultValue=""
                onChange={(e) => { videoSearchRef.current = e.target.value; }}
                onKeyPress={(e) => e.key === 'Enter' && handleVideoSearch()}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              onClick={handleVideoSearch}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Search
            </button>
            {(videoSearchRef.current || videoSearchTerm) && (
              <button
                onClick={handleVideoClear}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        {videoSearchTerm && (
          <div className="mb-3 text-sm text-sm text-gray-600">
            Found {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''} for "{videoSearchTerm}"
          </div>
        )}

        <div className="max-h-96 overflow-y-auto">
          <ItemList
            items={filteredVideos}
            renderItem={(video) => (
              <>
                <div>
                  <p className="font-medium text-gray-900">{video.video?.title}</p>
                  <p className="text-sm text-gray-500">
                    Views: {video.views_count} • {new Date(video.created_at).toLocaleDateString()}
                  </p>

                </div>
                <div className="flex flex-col items-end space-y-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    video.seen ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {video.total_played > 0 ? 'Watched' : 'Opened not watched'}
                  </span>
                  {video.user_video_suspended && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Suspended
                    </span>
                  )}
                </div>
              </>
            )}
            emptyMessage={videoSearchTerm ? "No videos match your search" : "No videos watched yet"}
            showAll={true}
          />
        </div>
      </SectionCard>
    );
  };

  const renderExams = () => (
    <SectionCard title="Exam History" icon={Award}>
      <ItemList
        items={exams}
        renderItem={(exam) => (
          <>
            <div>
              <p className="font-medium text-gray-900">{exam.name}</p>
              <p className="text-sm text-gray-500">
                {exam.question_number} questions • {exam.time ? `${exam.time} min` : 'No time limit'}
              </p>
              <p className="text-xs text-gray-400">
                {new Date(exam.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex flex-col items-end space-y-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                exam.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {exam.published ? 'Published' : 'Draft'}
              </span>
              {exam.redo_flag && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Retakeable
                </span>
              )}
            </div>
          </>
        )}
        emptyMessage="No exams taken yet"
      />
    </SectionCard>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'payments': return renderPayments();
      case 'attendance': return renderAttendance();
      case 'videos': return renderVideos();
      case 'exams': return renderExams();
      default: return renderOverview();
    }
  };

  const header = (
    <div className="bg-gradient-to-r from-orange-400 to-orange-500 px-6 py-4 flex items-center justify-between">
      <h2 className="text-2xl font-bold text-white">تقرير الطالب</h2>
      <div className="flex items-center space-x-3">
        <button
          onClick={() => dispatch(monthlyReportRequest({ student_id }))}
          disabled={isLoading}
          className="px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Sending...' : 'Send Monthly Report to Parent'}
        </button>
        <button
          onClick={closeFunc}
          className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
    </div>
  );

  const content = (
    <div className="w-full">
      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex space-x-1 px-6 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                                  className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-white text-orange-600 border-b-2 border-orange-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        ) : (
          renderTabContent()
        )}
      </div>

      {/* Special subject images for subject ID 34 */}
      {selectedData?.subject?.id == "34" && (
        <div className="p-6 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedData?.profile_url && (
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">Profile Image 1</p>
                <img 
                  src={`${baseUrl}/${selectedData.profile_url}`} 
                  alt="Profile 1"
                  className="max-w-full h-auto rounded-lg shadow-sm"
                />
              </div>
            )}
            {selectedData?.profile_url2 && (
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">Profile Image 2</p>
                <img 
                  src={`${baseUrl}/${selectedData.profile_url2}`} 
                  alt="Profile 2"
                  className="max-w-full h-auto rounded-lg shadow-sm"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <MainModel
      header={header}
      closeFunc={closeFunc}
      openVar={openUserData}
      buttonTitle={null}
      buttonFunc={null}
    >
      {content}
    </MainModel>
  );
};

export default StudentReport;
